/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type {
  ActionDescription,
  ConditionsConfig_ConditionConfig,
  ConditionsConfig_ConditionsConfigGroup,
  IntegrationState,
} from "@smartsuite/automation-engine-api";
import {
  ConditionsLogicOperator,
  ValueDescription_T,
} from "@smartsuite/automation-engine-api";
import * as R from "remeda";
import type { ActionListMerger } from ".";
import { OperatorDescriptionKinds } from "../../../../containers/AutomationForm/Forms/InputForm/FieldEditor/FieldEditorRow/containers/UpdateValueDropdown/constanst";
import type { ActionCandidate } from "../../../../types/candidate";
import { ejectReferences } from "../../utils/filters";

export const mergeReferenceIds =
  (
    actionHash: string,
    integrationId: string,
    actionId: string,
    nextHash: string,
  ): ActionListMerger =>
  (actions: ActionCandidate[]) =>
    R.pipe(
      actions,
      R.map((a) =>
        a.hash === actionHash
          ? {
              hash: nextHash,
              actionReference: {
                ...a.actionReference!,
                integrationId,
                actionId,
              },
              credentialId: "",
              description: {
                actionId: "",
                label: "",
                subLabel: "",
                authenticationId: [],
                inputs: [],
                outputs: [],
                conditionFields: [],
              },
              inputs: [],
              dependencies: [],
            }
          : a,
      ),
    );

export const mergeCredentialId =
  (actionHash: string, credentialId: string): ActionListMerger =>
  (actions: ActionCandidate[]) =>
    R.pipe(
      actions,
      R.map((a) =>
        a.hash === actionHash && a.credentialId !== credentialId
          ? {
              ...a,
              credentialId,
            }
          : a,
      ),
    );

export const mergeActionDescription =
  (actionHash: string, description: ActionDescription): ActionListMerger =>
  (actions: ActionCandidate[]) =>
    R.pipe(
      actions,
      R.map((a) =>
        a.hash === actionHash
          ? {
              ...a,
              description,
            }
          : a,
      ),
    );

export const mergeActionDefaultCondition =
  (actionHash: string): ActionListMerger =>
  (actions: ActionCandidate[]) =>
    R.pipe(
      actions,
      R.map((a) => {
        if (a.hash !== actionHash) {
          return a;
        }
        const newCondition: ConditionsConfig_ConditionsConfigGroup = {
          group: {
            oneofKind: "condition",
            condition: {
              conditionFieldId: "",
            },
          },
        };
        if (a.conditions === undefined) {
          return {
            ...a,
            conditions: {
              operator: ConditionsLogicOperator.OPERATOR_AND,
              group: [newCondition],
            },
          };
        }
        return {
          ...a,
          conditions: {
            ...a.conditions,
            group: [...a.conditions.group, newCondition],
          },
        };
      }),
    );

export const mergeUpdatedCondition =
  (
    actionHash: string,
    condition: ConditionsConfig_ConditionConfig,
    index: number,
  ): ActionListMerger =>
  (actions: ActionCandidate[]) =>
    R.pipe(
      actions,
      R.map((action) => {
        if (action.hash !== actionHash) {
          return action;
        }
        return {
          ...action,
          conditions: {
            ...action.conditions!,
            group: action.conditions!.group.map((item, i) =>
              i === index
                ? {
                    group: {
                      oneofKind: "condition",
                      condition,
                    },
                  }
                : item,
            ),
          },
        };
      }),
    );

export const ejectCondition =
  (actionHash: string, index: number): ActionListMerger =>
  (actions: ActionCandidate[]) =>
    R.pipe(
      actions,
      R.map((action) => {
        if (action.hash !== actionHash) {
          return action;
        }
        return {
          ...action,
          conditions: {
            ...action.conditions!,
            group: action.conditions!.group.filter((_, i) => i !== index),
          },
        };
      }),
    );

export const switchConditionOperator =
  (actionHash: string): ActionListMerger =>
  (actions: ActionCandidate[]) =>
    R.pipe(
      actions,
      R.map((action) => {
        if (action.hash !== actionHash) {
          return action;
        }
        return {
          ...action,
          conditions: {
            ...action.conditions!,
            operator:
              action.conditions?.operator ===
              ConditionsLogicOperator.OPERATOR_AND
                ? ConditionsLogicOperator.OPERATOR_OR
                : ConditionsLogicOperator.OPERATOR_AND,
          },
        };
      }),
    );

export const mergeActionName =
  (actionHash: string, actionName: string): ActionListMerger =>
  (actions: ActionCandidate[]) =>
    R.pipe(
      actions,
      R.map((a) =>
        a.hash === actionHash
          ? {
              ...a,
              actionName,
            }
          : a,
      ),
    );

export const mergeActionAccessError =
  (actionHash: string, accessError: boolean): ActionListMerger =>
  (actions: ActionCandidate[]) =>
    R.pipe(
      actions,
      R.map((a) =>
        a.hash === actionHash
          ? {
              ...a,
              accessError,
            }
          : a,
      ),
    );

export const mergeActionDeletedError =
  (actionHash: string, deletedError: boolean): ActionListMerger =>
  (actions: ActionCandidate[]) =>
    R.pipe(
      actions,
      R.map((a) =>
        a.hash === actionHash
          ? {
              ...a,
              deletedError,
            }
          : a,
      ),
    );

export const mergeActionTriggerAllowedError =
  (actionHash: string, triggerAllowedError: boolean): ActionListMerger =>
  (actions: ActionCandidate[]) =>
    R.pipe(
      actions,
      R.map((a) =>
        a.hash === actionHash
          ? {
              ...a,
              triggerAllowedError,
            }
          : a,
      ),
    );

export const mergeActionDisabledError =
  (actionHash: string, actionDisabled: boolean): ActionListMerger =>
  (actions: ActionCandidate[]) =>
    R.pipe(
      actions,
      R.map((a) =>
        a.hash === actionHash
          ? {
              ...a,
              actionDisabled,
            }
          : a,
      ),
    );

export const mergeIntegrationDisabledError =
  (actionHash: string, integrationDisabled: boolean): ActionListMerger =>
  (actions: ActionCandidate[]) =>
    R.pipe(
      actions,
      R.map((a) =>
        a.hash === actionHash
          ? {
              ...a,
              integrationDisabled,
            }
          : a,
      ),
    );

export const mergeAction =
  (action: ActionCandidate): ActionListMerger =>
  (actions: ActionCandidate[]) =>
    R.pipe(
      actions,
      R.map((a) => (a.hash === action.hash ? action : a)),
    );

export const mergeActionWithNoActionReferences =
  (action: ActionCandidate): ActionListMerger =>
  (actions: ActionCandidate[]) =>
    R.pipe(
      actions,
      R.map((a) =>
        a.hash === action.hash ? ejectReferences(action, "actionReference") : a,
      ),
    );

export const ejectEmptyInputs =
  (actionHash: string): ActionListMerger =>
  (actions: ActionCandidate[]) =>
    R.pipe(
      actions,
      R.map((action) => {
        if (action.hash !== actionHash) {
          return action;
        }
        return {
          ...action,
          inputs: R.filter(
            action.inputs,
            (input) =>
              input.value?.values.length !== 0 ||
              input.operatorConfig?.operator.oneofKind ===
                OperatorDescriptionKinds.CLEAR ||
              // An empty object/array is a deliberately chosen value, not an
              // unset input. Keep it, otherwise a re-merge (e.g. after a dynamic
              // description refresh) re-adds it as a new input with a reset
              // valueDescription and the chosen type is lost.
              input.valueDescription?.type === ValueDescription_T.KEY_VALUE ||
              input.valueDescription?.type === ValueDescription_T.LIST,
          ),
        };
      }),
    );

// See mergeIntegrationState for the trigger.
export const mergeActionIntegrationState =
  (
    actionHash: string,
    integrationState: IntegrationState | undefined,
  ): ActionListMerger =>
  (actions: ActionCandidate[]) =>
    integrationState === undefined
      ? actions
      : actions.map((a) =>
          a.hash === actionHash ? { ...a, integrationState } : a,
        );
